import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '../../../components/Card';
import { StatCard } from '../../../components/StatCard';
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import apiClient from '../../../lib/api';
import { CHART, CHART_AXIS, CHART_GRID, CHART_TOOLTIP, CHART_TOOLTIP_ITEM } from '../../../lib/chartColors';
import { fmtMinutes } from './attendanceUi';

const WEEKS = 6;

/** Monday 00:00 of the week containing `d`. */
function mondayOf(d: Date) {
    const x = new Date(d);
    const dow = (x.getDay() + 6) % 7; // 0 = Monday
    x.setDate(x.getDate() - dow);
    x.setHours(0, 0, 0, 0);
    return x;
}
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function weekLabel(start: Date) {
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return start.getMonth() === end.getMonth()
        ? `${MON[start.getMonth()]} ${start.getDate()}–${end.getDate()}`
        : `${MON[start.getMonth()]} ${start.getDate()} – ${MON[end.getMonth()]} ${end.getDate()}`;
}

interface Week { start: Date; label: string; totalMin: number; workedDays: number; avgMin: number; isCurrent: boolean }

/**
 * The employee's working hours grouped into the last six calendar weeks
 * (Mon–Sun), with this-week-vs-last-week comparison and a six-week trend.
 * Computed from recorded attendance only.
 */
export function WeeklySummary({ employeeId }: { employeeId?: string }) {
    const [weeks, setWeeks] = useState<Week[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            const today = new Date();
            const thisMon = mondayOf(today);
            const from = new Date(thisMon); from.setDate(thisMon.getDate() - (WEEKS - 1) * 7);
            const thisKey = iso(thisMon);
            try {
                const res = await apiClient.get('/people/attendance/history', {
                    params: { from: iso(from), to: iso(today), employeeId },
                });
                const days: any[] = res.data?.data ?? res.data ?? [];
                const buckets = new Map<string, Week>();
                for (let i = 0; i < WEEKS; i++) {
                    const s = new Date(from); s.setDate(from.getDate() + i * 7);
                    const key = iso(s);
                    buckets.set(key, { start: s, label: weekLabel(s), totalMin: 0, workedDays: 0, avgMin: 0, isCurrent: key === thisKey });
                }
                for (const d of days) {
                    const wk = iso(mondayOf(new Date(d.date)));
                    const b = buckets.get(wk);
                    if (!b) continue;
                    const wm = Number(d.summary?.worked_minutes ?? d.summary?.workedMinutes ?? 0);
                    b.totalMin += wm;
                    if (wm > 0) b.workedDays += 1;
                }
                const out = [...buckets.values()].map((b) => ({ ...b, avgMin: b.workedDays ? Math.round(b.totalMin / b.workedDays) : 0 }));
                if (!cancelled) setWeeks(out);
            } catch (e: any) {
                if (!cancelled) setError(e?.response?.data?.message || 'Could not load weekly hours');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [employeeId]);

    const thisWeek = weeks[weeks.length - 1];
    const lastWeek = weeks[weeks.length - 2];
    const sixAvg = useMemo(() => {
        const active = weeks.filter((w) => w.workedDays > 0);
        if (!active.length) return 0;
        return Math.round(active.reduce((n, w) => n + w.avgMin, 0) / active.length);
    }, [weeks]);

    const delta = thisWeek && lastWeek && lastWeek.avgMin > 0
        ? Math.round(((thisWeek.avgMin - lastWeek.avgMin) / lastWeek.avgMin) * 100)
        : null;

    const chartData = weeks.map((w) => ({ label: w.label, hours: Math.round((w.avgMin / 60) * 100) / 100, isCurrent: w.isCurrent }));

    const deltaTone = delta == null ? 'text-muted-foreground' : delta > 0 ? 'text-success-strong' : delta < 0 ? 'text-destructive-strong' : 'text-muted-foreground';
    const DeltaIcon = delta == null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;

    if (loading) {
        return (
            <Card>
                <div className="flex items-center gap-2 px-4 py-8 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" /> Loading weekly hours…
                </div>
            </Card>
        );
    }
    if (error) {
        return <Card><p className="px-4 py-8 text-center text-sm text-destructive-strong">{error}</p></Card>;
    }

    return (
        <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
                <StatCard
                    label="This week — avg / day"
                    value={fmtMinutes(thisWeek?.avgMin ?? 0)}
                    hint={`${fmtMinutes(thisWeek?.totalMin ?? 0)} over ${thisWeek?.workedDays ?? 0} day${(thisWeek?.workedDays ?? 0) === 1 ? '' : 's'}`}
                />
                <StatCard
                    label="Last week — avg / day"
                    value={fmtMinutes(lastWeek?.avgMin ?? 0)}
                    hint={`${fmtMinutes(lastWeek?.totalMin ?? 0)} over ${lastWeek?.workedDays ?? 0} day${(lastWeek?.workedDays ?? 0) === 1 ? '' : 's'}`}
                />
                <StatCard
                    label="6-week average / day"
                    value={fmtMinutes(sixAvg)}
                    hint={
                        delta == null ? 'No prior week to compare'
                            : `${delta > 0 ? '+' : ''}${delta}% vs last week`
                    }
                />
            </div>

            <Card>
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <div>
                        <h2 className="font-display text-card-title text-foreground">Weekly working hours</h2>
                        <p className="text-xs text-muted-foreground">Average hours per working day, last {WEEKS} weeks.</p>
                    </div>
                    {delta != null && (
                        <span className={`inline-flex items-center gap-1 text-sm font-medium ${deltaTone}`}>
                            <DeltaIcon className="size-4" />
                            {delta > 0 ? '+' : ''}{delta}% <span className="text-muted-foreground font-normal">vs last week</span>
                        </span>
                    )}
                </div>
                {chartData.every((d) => d.hours === 0) ? (
                    <p className="px-4 py-10 text-center text-sm text-muted-foreground">No attendance recorded in the last {WEEKS} weeks.</p>
                ) : (
                    <div className="h-56 px-2 py-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                                <XAxis dataKey="label" stroke={CHART_AXIS} fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke={CHART_AXIS} fontSize={12} tickLine={false} axisLine={false} width={40}
                                    allowDecimals={false} tickFormatter={(v: number) => `${v}h`} />
                                <Tooltip contentStyle={CHART_TOOLTIP} itemStyle={CHART_TOOLTIP_ITEM} cursor={{ fill: 'hsl(var(--secondary))' }}
                                    formatter={(v: number) => [fmtMinutes(Math.round(v * 60)), 'Avg / day']} />
                                <Bar dataKey="hours" radius={[4, 4, 0, 0]} maxBarSize={48}>
                                    {chartData.map((d, i) => (
                                        <Cell key={i} fill={d.isCurrent ? CHART.blue : 'hsl(var(--muted-foreground))'} fillOpacity={d.isCurrent ? 1 : 0.35} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>
        </div>
    );
}
